import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/lib/utils';

import type { BulkCreateParams, Interviewer } from '@/types/scheduling.types';

interface BulkCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (params: BulkCreateParams) => Promise<void>;
  interviewers: Interviewer[];
}

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const DEFAULT_DAYS = [1, 2, 3, 4, 5];
const DURATION_OPTIONS = [15, 30, 45, 60] as const;

const bulkSchema = z
  .object({
    interviewerUserId: z.string().min(1),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
    daysOfWeek: z.array(z.number()).min(1),
    startTime: z.string().min(1),
    endTime: z.string().min(1),
    slotDurationMin: z.number().min(15),
    capacity: z.number().min(1),
  })
  .refine((d) => d.endTime > d.startTime, { message: 'endAfterStart', path: ['endTime'] })
  .refine((d) => d.endDate >= d.startDate, { message: 'dateRangeRequired', path: ['endDate'] });

interface FormData {
  interviewerUserId: string;
  startDate: string;
  endDate: string;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  slotDurationMin: number;
  capacity: number;
}

function buildInitialFormData(): FormData {
  return {
    interviewerUserId: '',
    startDate: '',
    endDate: '',
    daysOfWeek: [...DEFAULT_DAYS],
    startTime: '09:00',
    endTime: '17:00',
    slotDurationMin: 30,
    capacity: 1,
  };
}

function countSlots(formData: FormData): number {
  const { startDate, endDate, daysOfWeek, startTime, endTime, slotDurationMin } = formData;
  if (!startDate || !endDate || !startTime || !endTime || daysOfWeek.length === 0 || slotDurationMin <= 0) {
    return 0;
  }

  const [startHour, startMin] = startTime.split(':').map(Number) as [number, number];
  const [endHour, endMin] = endTime.split(':').map(Number) as [number, number];
  const totalMinutes = endHour * 60 + endMin - (startHour * 60 + startMin);
  if (totalMinutes <= 0) return 0;

  const slotsPerDay = Math.floor(totalMinutes / slotDurationMin);
  if (slotsPerDay <= 0) return 0;

  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    if (daysOfWeek.includes(current.getDay())) {
      count += slotsPerDay;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

const labelClassName = 'block text-sm font-medium text-gray-700';
const inputClassName =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

export function BulkCreateModal({ isOpen, onClose, onSave, interviewers }: BulkCreateModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(buildInitialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialFormData());
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const previewCount = useMemo(() => countSlots(formData), [formData]);

  const toggleDay = useCallback((day: number) => {
    setFormData((prev) => {
      const has = prev.daysOfWeek.includes(day);
      return {
        ...prev,
        daysOfWeek: has ? prev.daysOfWeek.filter((d) => d !== day) : [...prev.daysOfWeek, day],
      };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});

      const result = bulkSchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const path = issue.path.join('.');
          fieldErrors[path] = t(`scheduling.bulkCreateModal.validation.${issue.message}`, issue.message);
        }
        setErrors(fieldErrors);
      } else {
        setIsSubmitting(true);
        try {
          const params: BulkCreateParams = {
            interviewerUserId: result.data.interviewerUserId,
            startDate: result.data.startDate,
            endDate: result.data.endDate,
            daysOfWeek: result.data.daysOfWeek,
            startTime: result.data.startTime,
            endTime: result.data.endTime,
            slotDurationMin: result.data.slotDurationMin,
            capacity: result.data.capacity,
          };
          await onSave(params);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formData, onSave, t],
  );

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>{t('scheduling.bulkCreateModal.title')}</h2>
          <button onClick={onClose} className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            {/* Interviewer */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.interviewer')}</label>
              <select
                value={formData.interviewerUserId}
                onChange={(e) => setFormData((prev) => ({ ...prev, interviewerUserId: e.target.value }))}
                className={cn(inputClassName, errors['interviewerUserId'] && 'border-red-500')}>
                <option value=''>{t('scheduling.bulkCreateModal.interviewerPlaceholder')}</option>
                {interviewers.map((interviewer) => (
                  <option key={interviewer.userId} value={interviewer.userId}>
                    {interviewer.name}
                  </option>
                ))}
              </select>
              {errors['interviewerUserId'] && (
                <p className='mt-1 text-xs text-red-600'>{errors['interviewerUserId']}</p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.startDate')}</label>
              <input
                type='date'
                value={formData.startDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                className={cn(inputClassName, errors['startDate'] && 'border-red-500')}
              />
              {errors['startDate'] && <p className='mt-1 text-xs text-red-600'>{errors['startDate']}</p>}
            </div>

            {/* End Date */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.endDate')}</label>
              <input
                type='date'
                value={formData.endDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                className={cn(inputClassName, errors['endDate'] && 'border-red-500')}
              />
              {errors['endDate'] && <p className='mt-1 text-xs text-red-600'>{errors['endDate']}</p>}
            </div>

            {/* Days of Week */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.daysOfWeek')}</label>
              <div className='mt-2 flex flex-wrap gap-4'>
                {DAYS_OF_WEEK.map((day) => {
                  const dayKey = DAY_KEYS[day];
                  return (
                    <label key={day} className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='checkbox'
                        checked={formData.daysOfWeek.includes(day)}
                        onChange={() => toggleDay(day)}
                        className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                      />
                      {t(`scheduling.days.${dayKey}`)}
                    </label>
                  );
                })}
              </div>
              {errors['daysOfWeek'] && <p className='mt-1 text-xs text-red-600'>{errors['daysOfWeek']}</p>}
            </div>

            {/* Start Time */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.startTime')}</label>
              <input
                type='time'
                value={formData.startTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                className={cn(inputClassName, errors['startTime'] && 'border-red-500')}
              />
              {errors['startTime'] && <p className='mt-1 text-xs text-red-600'>{errors['startTime']}</p>}
            </div>

            {/* End Time */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.endTime')}</label>
              <input
                type='time'
                value={formData.endTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                className={cn(inputClassName, errors['endTime'] && 'border-red-500')}
              />
              {errors['endTime'] && <p className='mt-1 text-xs text-red-600'>{errors['endTime']}</p>}
            </div>

            {/* Slot Duration */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.slotDuration')}</label>
              <select
                value={formData.slotDurationMin}
                onChange={(e) => setFormData((prev) => ({ ...prev, slotDurationMin: Number(e.target.value) }))}
                className={cn(inputClassName, errors['slotDurationMin'] && 'border-red-500')}>
                {DURATION_OPTIONS.map((duration) => (
                  <option key={duration} value={duration}>
                    {t(`scheduling.durations.${duration}`)}
                  </option>
                ))}
              </select>
              {errors['slotDurationMin'] && <p className='mt-1 text-xs text-red-600'>{errors['slotDurationMin']}</p>}
            </div>

            {/* Capacity Per Slot */}
            <div>
              <label className={labelClassName}>{t('scheduling.bulkCreateModal.capacity')}</label>
              <input
                type='number'
                min={1}
                value={formData.capacity}
                onChange={(e) => setFormData((prev) => ({ ...prev, capacity: Number(e.target.value) }))}
                className={cn(inputClassName, errors['capacity'] && 'border-red-500')}
              />
              {errors['capacity'] && <p className='mt-1 text-xs text-red-600'>{errors['capacity']}</p>}
            </div>

            {/* Preview */}
            <div className='sm:col-span-2'>
              <p className='text-sm font-medium text-blue-700'>
                {t('scheduling.bulkCreateModal.preview', { count: previewCount })}
              </p>
            </div>
          </div>
        </form>

        <div className='flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting}
            className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            {t('scheduling.bulkCreateModal.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
            {isSubmitting ? t('scheduling.bulkCreateModal.creating') : t('scheduling.bulkCreateModal.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
